import { useState } from "react";
import html2canvas from "html2canvas-pro";
import { MessageSquareHeart, Camera, Send, X, Loader2, Bug, Lightbulb, Heart, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";

import { log } from "@/lib/log";
const CATEGORIES = [
  { key: "bug", label: "Bug", Icon: Bug, tint: "text-rp-red" },
  { key: "idea", label: "Idea", Icon: Lightbulb, tint: "text-amber-400" },
  { key: "praise", label: "Praise", Icon: Heart, tint: "text-rose-400" },
  { key: "other", label: "Other", Icon: MessageCircle, tint: "text-rp-textDim" },
];

/**
 * Floating feedback bubble — bottom-right. Captures a viewport screenshot via
 * html2canvas-pro, lets the user annotate with a category + message, and
 * POSTs to `/api/feedback`. Admin can review at /admin/feedback.
 *
 * Screenshot is captured BEFORE opening the modal so the modal itself doesn't
 * appear in the image.
 */
export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState(null); // base64 data URL
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("other");
  const [sending, setSending] = useState(false);

  const beginFeedback = async () => {
    setCapturing(true);
    setScreenshot(null);
    try {
      // Hide the bubble itself so it doesn't appear in the screenshot.
      const bubble = document.getElementById("rp-feedback-bubble");
      const prev = bubble?.style.display;
      if (bubble) bubble.style.display = "none";
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        scale: Math.min(1, window.devicePixelRatio || 1),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      });
      if (bubble) bubble.style.display = prev || "";
      // Downscale to ~1200px max width to keep payload light.
      const maxW = 1200;
      const ratio = canvas.width > maxW ? maxW / canvas.width : 1;
      const scaled = document.createElement("canvas");
      scaled.width = Math.round(canvas.width * ratio);
      scaled.height = Math.round(canvas.height * ratio);
      scaled.getContext("2d").drawImage(canvas, 0, 0, scaled.width, scaled.height);
      const dataUrl = scaled.toDataURL("image/jpeg", 0.7);
      setScreenshot(dataUrl);
    } catch (e) {
      // Continue without screenshot — still allow text-only feedback.
      log.warn("[FeedbackWidget] screenshot failed:", e);
      toast.warning("Couldn't capture screenshot — you can still send text feedback");
    } finally {
      setCapturing(false);
      setOpen(true);
    }
  };

  const submit = async () => {
    if (message.trim().length < 2) {
      toast.error("Tell us a little more in the message");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        category,
        page_url: window.location.href,
        screenshot_b64: screenshot,
        user_agent: navigator.userAgent,
      });
      toast.success("Thanks! Feedback sent");
      setOpen(false);
      setMessage("");
      setScreenshot(null);
      setCategory("other");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send feedback");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        id="rp-feedback-bubble"
        onClick={beginFeedback}
        disabled={capturing}
        data-testid="feedback-bubble"
        aria-label="Send feedback with screenshot"
        title="Send feedback"
        className="fixed bottom-5 left-5 z-40 grid h-12 w-12 place-items-center rounded-full bg-rp-accent text-white shadow-[0_10px_30px_rgba(79,124,255,0.4)] hover:bg-rp-accentHover transition-transform hover:scale-105 disabled:opacity-60"
      >
        {capturing ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageSquareHeart className="h-5 w-5" />}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-rp-accent" /> Send feedback
            </DialogTitle>
            <DialogDescription>
              We captured this screenshot from your current view. Add a note so we can act on it.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2" data-testid="feedback-categories">
            {CATEGORIES.map(({ key, label, Icon, tint }) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`feedback-cat-${key}`}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                  category === key
                    ? "border-rp-accent bg-rp-accent/10 text-rp-text"
                    : "border-rp-border bg-rp-surface2 text-rp-textDim hover:text-rp-text"
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${tint}`} />
                {label}
              </button>
            ))}
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What happened? What did you expect to happen?"
            rows={4}
            maxLength={4000}
            data-testid="feedback-message"
            className="w-full rounded-md border border-rp-border bg-rp-surface px-3 py-2 text-sm text-rp-text focus:border-rp-accent focus:outline-none"
          />

          {screenshot && (
            <div
              className="relative rounded-lg border border-rp-border overflow-hidden"
              data-testid="feedback-screenshot-preview"
            >
              <img src={screenshot} alt="Screenshot preview" className="max-h-[200px] w-full object-cover object-top" />
              <button
                onClick={() => setScreenshot(null)}
                aria-label="Remove screenshot"
                data-testid="feedback-screenshot-remove"
                className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-rp-bg/80 text-rp-text hover:bg-rp-red"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <span className="absolute bottom-2 left-2 rounded bg-rp-bg/80 px-2 py-0.5 text-[10px] uppercase tracking-wider text-rp-textDim">
                Screenshot attached
              </span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="feedback-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submit} disabled={sending} data-testid="feedback-send-btn" className="bg-rp-accent hover:bg-rp-accentHover">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Send className="mr-2 h-4 w-4" /> Send</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
